import React, { Component } from 'react';
import PropTypes from 'prop-types';
import cloneDeep from 'lodash/cloneDeep';
import pick from 'lodash/pick';
import isEqual from 'lodash/isEqual';
import AdvForm from '../../../CommonComponents/AdvForm';
import Footer from '../../Footer/FooterContainer';
import {
  simpleSelectDefaultValue,
  advInputTextChanged,
  simpleSelectChangedToNonNull,
} from '../../../Profile/Accounts_react/AccountsUtils';
import PlanImpact from '../../PlanImpact/PlanImpactContainer';
import Loading from '../../Loading/LoadingView';
import {
  isNumeric,
  isNumericFormatted,
  isUuid,
  createCustomPropType,
} from '../../../../js/app/react/validators';
import ValidationErrors from '../../ValidationErrors/ValidationErrorsContainer';
import AdvInputWrapper from '../../../CommonComponents/AdvInputWrapper';
import AdvInput from '../../../CommonComponents/AdvInput';
import { Select } from '../../../CommonComponents/OPL/Select/Select/Select';

export const shouldQueryPlanImpact = (previousProps, props) => {
  const impactTriggerProps = ['whatIf', 'fpId', 'isOPLMonteCarlo'];
  const pickedProps = cloneDeep(pick(props, impactTriggerProps));
  const pickedPreviousProps = cloneDeep(pick(previousProps, impactTriggerProps));

  if (isEqual(pickedPreviousProps, pickedProps)) {
    return false;
  }

  if (props.hasRetirementExpensesAmount) {
    let restore;
    if (!previousProps.hasRetirementExpensesAmount) {
      // Only if it's the only difference
      restore = pickedPreviousProps.whatIf.RetirementExpenses;
      pickedPreviousProps.whatIf.RetirementExpenses = pickedProps.whatIf.RetirementExpenses;
      if (
        isEqual(pickedPreviousProps, pickedProps) &&
        !isNumericFormatted(pickedProps.whatIf.RetirementExpenseAmount)
      ) {
        return false;
      }
      pickedPreviousProps.whatIf.RetirementExpenses = restore;
    }

    if (!isNumericFormatted(pickedProps.whatIf.RetirementExpenseAmount)) {
      return false;
    }
  }
  return true;
};

class RetirementExpensesView extends Component {
  componentDidMount() {
    const {
      fetchData,
      clientId,
      fpId,
      goalId,
      fetchInitialPlanImpact,
      whatIf,
      whatIfTemplate,
    } = this.props;
    fetchData(clientId, fpId, goalId);
    fetchInitialPlanImpact(clientId, fpId, goalId, whatIf, whatIfTemplate);
  }

  componentDidUpdate(previousProps) {
    const { clientId, fpId, goalId, whatIf, fetchPlanImpact, whatIfTemplate } = this.props;

    if (shouldQueryPlanImpact(previousProps, this.props)) {
      fetchPlanImpact(clientId, fpId, goalId, whatIf, whatIfTemplate);
    }
  }

  render() {
    const {
      goalId,
      clientId,
      hasRetirementExpensesAmount,
      retirementExpensesAmount,
      retirementExpensesOptions,
      retirementExpenses,
      storeData,
      loading,
      planImpactItems,
      title,
      groupTitle,
      fpId,
      whatIf,
      whatIfTemplate,
      applyWhatIf,
      areOptionsLoaded,
    } = this.props;

    return (
      <div className="what-ifs-layout">
        {loading && <Loading />}
        <div className="what-ifs-section what-ifs-layout-header">
          <h3>What-If...</h3>
        </div>
        <div className="what-ifs-layout-content">
          <h4 className="what-if-group-title">{groupTitle}</h4>
          <AdvForm>
            <ValidationErrors clientId={clientId} />
            <ul className={`what-ifs-form-fields what-ifs-box${areOptionsLoaded ? ' loaded' : ''}`}>
              <li className="what-ifs-form-field">
                <h5 className="what-if-title">{title}</h5>
              </li>
              <li className="what-ifs-form-field">
                <Select
                  isPortal
                  value={simpleSelectDefaultValue(retirementExpensesOptions, retirementExpenses)}
                  onChange={simpleSelectChangedToNonNull(
                    clientId,
                    storeData(goalId),
                    'WhatIf.RetirementExpenses',
                  )}
                  options={retirementExpensesOptions}
                  aria-label="Retirement Expenses"
                />
              </li>
              {hasRetirementExpensesAmount && (
                <li className="what-ifs-form-field">
                  <AdvInputWrapper hasCurrency hasPerYear className="what-ifs-input-wrapper">
                    <AdvInput
                      type="text"
                      dataType="money"
                      maxLength="14"
                      onBlur={formattedExpenses => {
                        // formattedExpenses is something like 47,000
                        // We want to save it not as a formatted string but as an integer instead
                        const expenses = parseInt(formattedExpenses.replace(/,/g, ''), 10);

                        advInputTextChanged(
                          clientId,
                          storeData(goalId, whatIfTemplate.WhatIfType),
                          'WhatIf.RetirementExpenseAmount',
                        )(expenses);
                      }}
                      nullable
                      value={retirementExpensesAmount}
                      className="what-ifs-input"
                    />
                  </AdvInputWrapper>
                </li>
              )}
            </ul>
          </AdvForm>
        </div>
        <PlanImpact clientId={clientId} planImpactItems={planImpactItems} />
        <Footer
          clientId={clientId}
          applyToPlanCallback={applyWhatIf(clientId, fpId, goalId, whatIf, whatIfTemplate)}
        />
      </div>
    );
  }
}
RetirementExpensesView.defaultProps = {
  title: 'Untitled What If',
  groupTitle: 'Untitled What If Group',
};

RetirementExpensesView.propTypes = {
  goalId: createCustomPropType(isUuid).isRequired,
  fpId: createCustomPropType(isUuid).isRequired,
  clientId: createCustomPropType(isNumeric).isRequired,
  title: PropTypes.string,
  groupTitle: PropTypes.string,
  areOptionsLoaded: PropTypes.bool.isRequired,

  hasRetirementExpensesAmount: PropTypes.bool.isRequired,
  retirementExpensesAmount: createCustomPropType(isNumeric).isRequired,
  retirementExpensesOptions: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  retirementExpenses: PropTypes.number.isRequired,

  whatIf: PropTypes.shape({}).isRequired,
  whatIfTemplate: PropTypes.shape({}).isRequired,
  storeData: PropTypes.func.isRequired,
  fetchData: PropTypes.func.isRequired,
  loading: PropTypes.bool.isRequired,
  fetchPlanImpact: PropTypes.func.isRequired,
  fetchInitialPlanImpact: PropTypes.func.isRequired,
  planImpactItems: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      current: PropTypes.string.isRequired,
      proposed: PropTypes.string.isRequired,
    }),
  ).isRequired,
  applyWhatIf: PropTypes.func.isRequired,
};

export default RetirementExpensesView;
